import { useMemo } from 'react'
import { getCreditTypeConfig, type CreditType } from './credit-type-utils'
import { getFormattedCredits } from '@/features/subscription/billing/billing-plans/billing-plan/utils/billing-plan-helper'
import { useBillingUser } from '@/features/subscription/billing/billing-user-context'

export function useCredits() {
    const billingUser = useBillingUser()
    const billingAndUsage = billingUser?.billingAndUsage

    const creditSegments = useMemo(() => {
        if (!billingAndUsage?.usage?.credits.creditPools) return []

        const groupedPools = billingAndUsage.usage.credits.creditPools.reduce(
            (acc, pool) => {
                const type = pool.type as CreditType
                const startsAt = pool.startsAt ? new Date(pool.startsAt).toISOString() : null
                const expiresAt = pool.expiresAt ? new Date(pool.expiresAt).toISOString() : null
                const groupKey = `${type}-${startsAt || 'null'}-${expiresAt || 'null'}`

                if (!acc[groupKey]) {
                    acc[groupKey] = {
                        type,
                        pools: [],
                        priority: pool.priority,
                        startsAt,
                        expiresAt,
                    }
                }
                acc[groupKey].pools.push(pool)
                acc[groupKey].priority = Math.min(acc[groupKey].priority, pool.priority)
                return acc
            },
            {} as Record<
                string,
                {
                    type: CreditType
                    pools: typeof billingAndUsage.usage.credits.creditPools
                    priority: number
                    startsAt: string | null
                    expiresAt: string | null
                }
            >
        )

        return Object.entries(groupedPools)
            .sort(([, a], [, b]) => a.priority - b.priority)
            .map(([, { type, pools, startsAt, expiresAt }]) => {
                const config = getCreditTypeConfig(type)

                const totalCredits = pools.reduce((sum, pool) => sum + pool.totalCredits, 0)
                const consumedCredits = pools.reduce((sum, pool) => sum + pool.consumedCredits, 0)
                const availableCredits = pools.reduce((sum, pool) => sum + pool.availableCredits, 0)

                const uuid = pools[0].uuid

                return {
                    uuid,
                    totalCredits,
                    consumedCredits,
                    availableCredits,
                    color: config.color,
                    label: config.label,
                    startsAt,
                    expiresAt,
                }
            })
    }, [billingAndUsage])

    const totalAvailable = useMemo(
        () => billingAndUsage?.usage?.credits.totalAvailable,
        [billingAndUsage?.usage?.credits.totalAvailable]
    )

    const activeCreditType = useMemo(() => {
        return creditSegments.find((segment) => segment.availableCredits > 0) || creditSegments[0]
    }, [creditSegments])

    if (!billingAndUsage?.usage?.credits) return

    return {
        totalAvailable: {
            credits: totalAvailable,
            formattedCredits: getFormattedCredits(totalAvailable),
        },
        creditSegments,
        activeCreditType,
        isTrial: billingAndUsage.billing.activeSubscription?.isInTrial,
        creditCosts: billingAndUsage?.usage?.credits.costs,
        currentUsedCredits: {
            credits: billingAndUsage?.usage?.credits.currentUsage,
            formattedCredits: getFormattedCredits(billingAndUsage?.usage?.credits.currentUsage),
        },
    }
}
